import React, { useState, useEffect } from 'react';
import { shoppingListService } from '../services/shoppingListService';
import { CategoryInputWithSuggestions } from '../components/CategoryInputWithSuggestions';
import Layout from '../../../shared/components/layout/Layout';
import { NotificationModal } from '../../../shared/components/ui';
import { useTranslation } from 'react-i18next';

const ShoppingList = () => {
    const { t } = useTranslation();
    const [currentWeekList, setCurrentWeekList] = useState(null);
    const [nextWeekList, setNextWeekList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentWeekInfo, setCurrentWeekInfo] = useState(null);
    const [nextWeekInfo, setNextWeekInfo] = useState(null);
    const [showAddItemForm, setShowAddItemForm] = useState(false);
    const [newItem, setNewItem] = useState({ name: '', quantity: '', category: '' });
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [activeTab, setActiveTab] = useState('current'); // 'current' o 'next'
    const [notification, setNotification] = useState({ isOpen: false, type: 'success', message: '' });
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, title: '', message: '' });

    useEffect(() => {
        fetchBothShoppingLists();
    }, []);

    const fetchBothShoppingLists = async () => {
        setLoading(true);
        setError(null);

        // Manejar cada request por separado para que una falla no afecte a la otra
        const fetchCurrentWeek = async () => {
            try {
                const response = await shoppingListService.getCurrentWeekShoppingList();
                if (response.success) {
                    setCurrentWeekList(response.shopping_list);
                    setCurrentWeekInfo({
                        start: response.week_start,
                        end: response.week_end,
                        alreadyExists: response.already_exists
                    });
                } else {
                    // No hay lista para la semana actual (error 400 es normal)
                    setCurrentWeekList(null);
                    setCurrentWeekInfo({
                        start: null,
                        end: null,
                        alreadyExists: false,
                        message: response.message || t('shoppingList.noListThisWeek.description')
                    });
                }
            } catch (err) {
                setCurrentWeekList(null);
                setCurrentWeekInfo({
                    start: null,
                    end: null,
                    alreadyExists: false,
                    message: t('shoppingList.errors.loadThisWeek')
                });
            }
        };

        const fetchNextWeek = async () => {
            try {
                const response = await shoppingListService.getNextWeekShoppingList();
                if (response.success) {
                    setNextWeekList(response.shopping_list);
                    setNextWeekInfo({
                        start: response.week_start,
                        end: response.week_end,
                        alreadyExists: response.already_exists
                    });
                } else {
                    // No hay lista para la semana siguiente (error 400 es normal)
                    setNextWeekList(null);
                    setNextWeekInfo({
                        start: null,
                        end: null,
                        alreadyExists: false,
                        message: response.message || t('shoppingList.noListNextWeek.description')
                    });
                }
            } catch (err) {
                setNextWeekList(null);
                setNextWeekInfo({
                    start: null,
                    end: null,
                    alreadyExists: false,
                    message: t('shoppingList.errors.loadNextWeek')
                });
            }
        };

        // Ejecutar ambas requests en paralelo, pero cada una maneja sus propios errores
        await Promise.allSettled([fetchCurrentWeek(), fetchNextWeek()]);

        setLoading(false);
    };

    // Cambiar automáticamente al tab correcto cuando cambian las listas disponibles
    useEffect(() => {
        // Si "esta semana" no está disponible y "próxima semana" sí, cambiar a "próxima semana"
        if (!currentWeekList && nextWeekList) {
            setActiveTab('next');
        }
        // Si "próxima semana" no está disponible y "esta semana" sí, cambiar a "esta semana"
        else if (currentWeekList && !nextWeekList) {
            setActiveTab('current');
        }
    }, [currentWeekList, nextWeekList]);

    const handleToggleItem = async (itemId, listType) => {
        // Prevenir toggle si la lista está completada
        const activeList = listType === 'current' ? currentWeekList : nextWeekList;
        if (activeList && activeList.is_completed) {
            return;
        }

        try {
            await shoppingListService.toggleShoppingListItem(itemId);

            // Actualizar estado local según el tipo de lista
            if (listType === 'current') {
                setCurrentWeekList(prev => ({
                    ...prev,
                    items: prev.items.map(item =>
                        item.id === itemId
                            ? { ...item, is_checked: !item.is_checked }
                            : item
                    ),
                    checked_items: prev.items.filter(item =>
                        item.id === itemId ? !item.is_checked : item.is_checked
                    ).length + (prev.items.find(i => i.id === itemId)?.is_checked ? 0 : 1)
                }));
            } else {
                setNextWeekList(prev => ({
                    ...prev,
                    items: prev.items.map(item =>
                        item.id === itemId
                            ? { ...item, is_checked: !item.is_checked }
                            : item
                    ),
                    checked_items: prev.items.filter(item =>
                        item.id === itemId ? !item.is_checked : item.is_checked
                    ).length + (prev.items.find(i => i.id === itemId)?.is_checked ? 0 : 1)
                }));
            }
        } catch (err) {
        }
    };

    const handleAddManualItem = async (e) => {
        e.preventDefault();

        if (!newItem.name || !newItem.quantity) {
            setNotification({ isOpen: true, type: 'error', message: t('shoppingList.errors.nameQuantityRequired') });
            return;
        }

        const activeList = activeTab === 'current' ? currentWeekList : nextWeekList;

        try {
            const response = await shoppingListService.addManualItem(
                activeList.id,
                newItem.name,
                newItem.quantity,
                newItem.category || null
            );

            if (response.success) {
                // Refrescar ambas listas
                await fetchBothShoppingLists();
                // Reset form
                setNewItem({ name: '', quantity: '', category: '' });
                setShowAddItemForm(false);
                setNotification({ isOpen: true, type: 'success', message: t('shoppingList.success.itemAdded') });
            }
        } catch (err) {
            setNotification({ isOpen: true, type: 'error', message: t('shoppingList.errors.addItem') + ' ' + err.message });
        }
    };

    const handleCompleteList = () => {
        const activeList = activeTab === 'current' ? currentWeekList : nextWeekList;
        const weekName = activeTab === 'current' ? t('shoppingList.thisWeek') : t('shoppingList.nextWeek');

        setConfirmModal({
            isOpen: true,
            title: t('shoppingList.completeList'),
            message: `¿${t('shoppingList.completeList')} de ${weekName}?`,
            action: async () => {
                try {
                    await shoppingListService.completeShoppingList(activeList.id);
                    await fetchBothShoppingLists();
                    setNotification({ isOpen: true, type: 'success', message: t('shoppingList.success.listCompleted') });
                } catch (err) {
                    setNotification({ isOpen: true, type: 'error', message: t('shoppingList.errors.completeList') + ' ' + err.message });
                }
            }
        });
    };

    const handleUncompleteList = () => {
        const activeList = activeTab === 'current' ? currentWeekList : nextWeekList;
        const weekName = activeTab === 'current' ? t('shoppingList.thisWeek') : t('shoppingList.nextWeek');

        setConfirmModal({
            isOpen: true,
            title: t('shoppingList.uncompleteList'),
            message: `¿${t('shoppingList.uncompleteList')} de ${weekName}?`,
            action: async () => {
                try {
                    await shoppingListService.uncompleteShoppingList(activeList.id);
                    await fetchBothShoppingLists();
                    setNotification({ isOpen: true, type: 'success', message: t('shoppingList.success.listUncompleted') });
                } catch (err) {
                    setNotification({ isOpen: true, type: 'error', message: t('shoppingList.errors.uncompleteList') + ' ' + err.message });
                }
            }
        });
    };

    const toggleItemDetails = (itemId) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    const formatDate = (dateString) => {
        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        return new Date(dateString).toLocaleDateString('es-ES', options);
    };

    const groupItemsByCategory = (items) => {
        const grouped = {};
        items.forEach(item => {
            const category = item.category || t('shoppingList.categories.other');
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(item);
        });
        return grouped;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            'Verduras y Hortalizas': '🥬',
            'Frutas': '🍎',
            'Carnes': '🥩',
            'Pescados y Mariscos': '🐟',
            'Lácteos': '🥛',
            'Legumbres': '🫘',
            'Cereales y Pasta': '🌾',
            'Aceites y Condimentos': '🫒',
            'Otros': '📦'
        };
        return icons[category] || '📦';
    };

    if (loading) {
        return (
            <Layout showAuthLinks={true}>
                <div className="max-w-4xl mx-auto">
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">{t('shoppingList.loading')}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout showAuthLinks={true}>
                <div className="max-w-4xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('common.error')}</h3>
                        <p className="text-red-600 mb-4">{error}</p>
                        <button
                            onClick={fetchBothShoppingLists}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                        >
                            {t('common.retry') || 'Reintentar'}
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!currentWeekList && !nextWeekList) {
        return (
            <Layout showAuthLinks={true}>
                <div className="max-w-4xl mx-auto">
                    <div className="text-center py-12">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
                            <svg className="w-16 h-16 text-blue-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                                {t('shoppingList.empty.title')}
                            </h2>
                            <p className="text-blue-600 mb-6 max-w-md mx-auto">
                                {t('shoppingList.empty.description')}
                            </p>
                            <div className="space-y-2 text-sm text-blue-500">
                                <p>{t('shoppingList.empty.steps.step1')}</p>
                                <p>{t('shoppingList.empty.steps.step2')}</p>
                                <p>{t('shoppingList.empty.steps.step3')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    const activeList = activeTab === 'current' ? currentWeekList : nextWeekList;
    const activeWeekInfo = activeTab === 'current' ? currentWeekInfo : nextWeekInfo;

    // Solo mostrar mensaje de "no hay lista" si realmente no hay datos en ningún tab
    // Si hay datos en algún tab, mostrar la interfaz normal con tabs deshabilitados
    if (!currentWeekList && !nextWeekList) {
        return (
            <Layout showAuthLinks={true}>
                <div className="max-w-4xl mx-auto">
                    <div className="text-center py-12">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
                            <svg className="w-16 h-16 text-blue-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h2 className="text-2xl font-semibold text-blue-800 mb-4">
                                📋 No hay listas de compra disponibles
                            </h2>
                            <p className="text-blue-600 mb-6 max-w-md mx-auto">
                                Confirma tu semana en la página <strong>Plan</strong> para generar tu lista de compra personalizada.
                            </p>
                            <div className="space-y-2 text-sm text-blue-500">
                                <p>• Ve a la página Plan</p>
                                <p>• Revisa tus menús de la próxima semana</p>
                                <p>• Haz clic en "Confirmar menú"</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    const groupedItems = activeList ? groupItemsByCategory(activeList.items) : {};
    const progress = activeList && activeList.total_items > 0
        ? Math.round((activeList.checked_items / activeList.total_items) * 100)
        : 0;

    return (
        <Layout showAuthLinks={true}>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-3">
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">🛒 {t('shoppingList.title')}</h1>
                            {activeWeekInfo && (
                                <p className="text-green-100 text-sm sm:text-base truncate">
                                    {activeTab === 'current' ? t('shoppingList.thisWeek') : t('shoppingList.nextWeek')}: {
                                        activeWeekInfo.start && activeWeekInfo.end
                                            ? `${formatDate(activeWeekInfo.start)} ${t('common.of')} ${formatDate(activeWeekInfo.end)}`
                                            : '-'
                                    }
                                </p>
                            )}
                        </div>
                        <div className="text-center sm:text-right flex-shrink-0">
                            {activeList ? (
                                <>
                                    <div className="text-3xl sm:text-4xl font-bold">{activeList.checked_items}/{activeList.total_items}</div>
                                    <div className="text-green-100 text-xs sm:text-sm">{t('shoppingList.itemsBought')}</div>
                                </>
                            ) : (
                                <>
                                    <div className="text-3xl sm:text-4xl font-bold">-/-</div>
                                    <div className="text-green-100 text-xs sm:text-sm">{t('shoppingList.notAvailable')}</div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="bg-green-800 rounded-full h-2 sm:h-3 overflow-hidden">
                        <div
                            className="bg-white h-full transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-green-100 text-xs sm:text-sm mt-1 sm:mt-2">{progress}% {t('common.progress.percentCompleted', { percent: '' }).replace('% completed', '').trim() || ''}</p>
                </div>

                {/* Tabs */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:mb-6">
                    <button
                        onClick={() => setActiveTab('current')}
                        disabled={!currentWeekList}
                        className={`flex-1 px-3 sm:px-4 py-3 rounded-lg font-medium transition min-h-[56px] text-sm sm:text-base ${!currentWeekList
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : activeTab === 'current'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
                            }`}
                    >
                        <span className="hidden sm:inline">📅 {t('shoppingList.thisWeek')}</span>
                        <span className="sm:hidden">📅 {t('shoppingList.thisWeek')}</span>
                        {currentWeekList && (
                            <span className="ml-2 text-xs sm:text-sm">
                                ({currentWeekList.checked_items}/{currentWeekList.total_items})
                            </span>
                        )}
                        {!currentWeekList && (
                            <span className="ml-2 text-xs">{t('shoppingList.notAvailable')}</span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('next')}
                        disabled={!nextWeekList}
                        className={`flex-1 px-3 sm:px-4 py-3 rounded-lg font-medium transition min-h-[56px] text-sm sm:text-base ${!nextWeekList
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : activeTab === 'next'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400'
                            }`}
                    >
                        <span className="hidden sm:inline">📅 {t('shoppingList.nextWeek')}</span>
                        <span className="sm:hidden">📅 {t('shoppingList.nextWeek')}</span>
                        {nextWeekList && (
                            <span className="ml-2 text-xs sm:text-sm">
                                ({nextWeekList.checked_items}/{nextWeekList.total_items})
                            </span>
                        )}
                        {!nextWeekList && (
                            <span className="ml-2 text-xs">{t('shoppingList.notAvailable')}</span>
                        )}
                    </button>
                </div>


                {/* Items by category */}
                {!activeList ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                        <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">
                            {activeTab === 'current' ? t('shoppingList.noListThisWeek.title') : t('shoppingList.noListNextWeek.title')}
                        </h3>
                        <p className="text-gray-500 mb-4">
                            {activeTab === 'current' ? t('shoppingList.noListThisWeek.description') : t('shoppingList.noListNextWeek.description')}
                        </p>
                        <p className="text-sm text-gray-400">
                            {t('shoppingList.empty.description')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(groupedItems).map(([category, items]) => (
                            <div key={category} className="bg-white rounded-xl shadow-md overflow-hidden">
                                {/* Category header */}
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
                                    <h2 className="font-semibold text-gray-800 flex items-center gap-2 text-sm sm:text-base">
                                        <span className="text-xl sm:text-2xl">{getCategoryIcon(category)}</span>
                                        <span className="flex-1 min-w-0 truncate">{category}</span>
                                        <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">
                                            {items.filter(i => i.is_checked).length}/{items.length}
                                        </span>
                                    </h2>
                                </div>

                                {/* Category items */}
                                <div className="divide-y divide-gray-100">
                                    {items.map((item) => (
                                        <div key={item.id} className="hover:bg-gray-50 transition">
                                            <div className="p-3 sm:p-4 flex items-start gap-3">
                                                {/* Checkbox */}
                                                <button
                                                    onClick={() => handleToggleItem(item.id, activeTab)}
                                                    disabled={activeList && activeList.is_completed}
                                                    className={`flex-shrink-0 p-2 -m-2 min-h-[44px] min-w-[44px] flex items-center justify-center ${activeList && activeList.is_completed ? 'cursor-not-allowed opacity-50' : ''}`}
                                                >
                                                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded border-2 flex items-center justify-center transition ${item.is_checked
                                                        ? 'bg-green-600 border-green-600'
                                                        : activeList && activeList.is_completed
                                                            ? 'border-gray-300 cursor-not-allowed'
                                                            : 'border-gray-300 hover:border-green-500 active:border-green-600'
                                                        }`}>
                                                        {item.is_checked && (
                                                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </button>

                                                {/* Item info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-baseline gap-2 flex-wrap">
                                                        <span className={`font-medium text-sm sm:text-base ${item.is_checked ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                            {item.name}
                                                        </span>
                                                        <span className={`text-xs sm:text-sm ${item.is_checked ? 'text-gray-400' : 'text-gray-600'}`}>
                                                            {item.quantity}
                                                        </span>
                                                    </div>

                                                    {/* Nota para ingredientes incontables */}
                                                    {item.notes && (
                                                        <div className="mt-1 flex items-start gap-1">
                                                            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="text-xs text-amber-600 italic">
                                                                {item.notes}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Meals info */}
                                                    {item.used_in_meals && item.used_in_meals.length > 0 && (
                                                        <div className="mt-1">
                                                            <button
                                                                onClick={() => toggleItemDetails(item.id)}
                                                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                            >
                                                                <span>Usado en {item.used_in_meals.length} comida{item.used_in_meals.length !== 1 ? 's' : ''}</span>
                                                                <svg
                                                                    className={`w-3 h-3 transition-transform ${expandedItems.has(item.id) ? 'rotate-180' : ''}`}
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            </button>

                                                            {expandedItems.has(item.id) && (
                                                                <div className="mt-2 pl-3 border-l-2 border-blue-200 space-y-1">
                                                                    {item.used_in_meals.map((meal, idx) => (
                                                                        <div key={idx} className="text-xs text-gray-600">
                                                                            <span className="font-medium">{meal.meal_name}</span>
                                                                            <span className="text-gray-400 mx-1">•</span>
                                                                            <span>{formatDate(meal.date)}</span>
                                                                            <span className="text-gray-400 mx-1">•</span>
                                                                            <span className="text-blue-600">{meal.quantity_needed}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Empty state */}
                {activeList && activeList.total_items === 0 && (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <div className="text-6xl mb-4">🛒</div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('shoppingList.emptyList.title')}</h3>
                        <p className="text-gray-500">{t('shoppingList.emptyList.description')}</p>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-6">
                    <button
                        onClick={() => setShowAddItemForm(!showAddItemForm)}
                        className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition font-medium flex items-center justify-center gap-2 min-h-[56px] text-sm sm:text-base"
                    >
                        <span className="text-xl">+</span>
                        <span className="hidden sm:inline">{t('shoppingList.addItem')}</span><span className="sm:hidden">{t('shoppingList.addItem')}</span>
                    </button>
                    <button
                        onClick={activeList && activeList.is_completed ? handleUncompleteList : handleCompleteList}
                        disabled={!activeList}
                        className={`flex-1 px-4 py-3 rounded-lg transition font-medium flex items-center justify-center gap-2 min-h-[56px] text-sm sm:text-base ${!activeList
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : activeList.is_completed
                                ? 'bg-orange-600 text-white hover:bg-orange-700 active:bg-orange-800'
                                : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                            }`}
                    >
                        <span className="text-xl">{activeList && activeList.is_completed ? '↩' : '✓'}</span>
                        <span className="hidden sm:inline">
                            {!activeList 
                                ? t('shoppingList.notAvailable') 
                                : activeList.is_completed 
                                    ? t('shoppingList.uncompleteList') 
                                    : t('shoppingList.completeList')}
                        </span>
                        <span className="sm:hidden">
                            {!activeList 
                                ? t('shoppingList.notAvailable') 
                                : activeList.is_completed 
                                    ? t('shoppingList.uncompleteList') 
                                    : t('shoppingList.completeList')}
                        </span>
                    </button>
                </div>

                {/* Add item form */}
                {showAddItemForm && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4 shadow-md">
                        <h3 className="font-semibold mb-3 text-gray-800 text-sm sm:text-base">{t('shoppingList.addItem')}</h3>
                        <form onSubmit={handleAddManualItem} className="space-y-3">
                            <div>
                                <input
                                    type="text"
                                    placeholder={t('shoppingList.itemName')}
                                    value={newItem.name}
                                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] text-sm sm:text-base"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder={t('shoppingList.quantity')}
                                    value={newItem.quantity}
                                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] text-sm sm:text-base"
                                />
                                <CategoryInputWithSuggestions
                                    value={newItem.category}
                                    onChange={(value) => setNewItem({ ...newItem, category: value })}
                                    placeholder={t('shoppingList.category')}
                                    className="px-3 py-2"
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition font-medium min-h-[44px] text-sm sm:text-base"
                                >
                                    {t('common.save')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddItemForm(false);
                                        setNewItem({ name: '', quantity: '', category: '' });
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 active:text-gray-900 transition min-h-[44px] text-sm sm:text-base border border-gray-300 rounded-lg sm:border-0"
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Notification Modal */}
                <NotificationModal
                    isOpen={notification.isOpen}
                    onClose={() => setNotification({ ...notification, isOpen: false })}
                    type={notification.type}
                    message={notification.message}
                />

                {/* Confirm Modal (usando NotificationModal con onConfirm) */}
                <NotificationModal
                    isOpen={confirmModal.isOpen}
                    onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                    onConfirm={confirmModal.action || (() => {})}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmText={t('common.confirm') || 'Confirmar'}
                    cancelText={t('common.cancel') || 'Cancelar'}
                    type="warning"
                />
            </div>
        </Layout>
    );
};

export default ShoppingList;
